import { useParams, Link, Navigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Clock,
  ArrowRight,
  ArrowLeft,
  User,
  ChevronRight,
  Quote,
} from "lucide-react";
import { getPostBySlug, getRelatedPosts } from "@/lib/blogData";

const slugifyHeading = (h: string) =>
  h.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const BlogPost = () => {
  const { slug = "" } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = getRelatedPosts(post.related);
  const url = `https://www.neilai.ai/blog/${post.slug}`;

  const postJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.image,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Organization", name: "Neil AI Global Solutions" },
    publisher: {
      "@type": "Organization",
      name: "Neil AI Global Solutions",
      logo: {
        "@type": "ImageObject",
        url: "https://www.neilai.ai/favicon.ico",
      },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    keywords: post.tags.join(", "),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.neilai.ai/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://www.neilai.ai/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: url,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${post.title} | Neil AI Blog`}
        description={post.excerpt}
        keywords={post.tags.join(", ")}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(postJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <div className="min-h-screen flex flex-col bg-[#05070A]">
        <Navigation />

        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#071A33] via-[#05070A] to-[#05070A]" />
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              background:
                "radial-gradient(60% 50% at 50% 0%, hsl(206 100% 50% / 0.22), transparent 70%)",
            }}
          />
          <div className="relative container mx-auto px-4 md:px-6 max-w-4xl">
            {/* Breadcrumbs */}
            <nav
              aria-label="Breadcrumb"
              className="text-xs text-slate-400 flex items-center gap-1.5 flex-wrap"
            >
              <Link to="/" className="hover:text-white">
                Home
              </Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-white">
                Blog
              </Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-500 line-clamp-1">{post.title}</span>
            </nav>

            <Badge className="mt-5 bg-[#00A3FF]/15 text-[#00A3FF] border border-[#00A3FF]/40 hover:bg-[#00A3FF]/15">
              {post.category}
            </Badge>
            <h1 className="mt-4 font-bold text-3xl sm:text-4xl md:text-5xl text-white leading-[1.1]">
              {post.title}
            </h1>
            <p className="mt-4 text-slate-300/90 text-base md:text-lg max-w-3xl">
              {post.excerpt}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" />
                {post.author}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {post.readTime}
              </span>
            </div>
          </div>
        </section>

        <main className="flex-grow container mx-auto px-4 md:px-6 max-w-4xl pb-24">
          {/* Hero image */}
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#05070A] mb-10 md:mb-12">
            <img
              src={post.image}
              alt={post.imageAlt}
              loading="eager"
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Table of contents */}
          <Card className="mb-10 p-5 md:p-6 bg-white/[0.03] backdrop-blur-xl border border-white/10">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[#00A3FF]">
              In this article
            </h2>
            <ol className="mt-3 space-y-2 text-sm">
              {post.sections.map((s, i) => (
                <li key={s.heading}>
                  <a
                    href={`#${slugifyHeading(s.heading)}`}
                    className="text-slate-300 hover:text-white inline-flex items-start gap-2"
                  >
                    <span className="text-[#00A3FF] font-semibold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{s.heading}</span>
                  </a>
                </li>
              ))}
            </ol>
          </Card>

          {/* Intro */}
          <p className="text-lg md:text-xl text-slate-200 leading-relaxed font-light">
            {post.intro}
          </p>

          {/* Sections */}
          <div className="mt-10 space-y-12">
            {post.sections.map((section, idx) => (
              <section key={section.heading} id={slugifyHeading(section.heading)}>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  {section.heading}
                </h2>
                <div className="mt-4 space-y-4 text-slate-300/95 leading-relaxed">
                  {section.body.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>

                {/* Midway pull quote + CTA */}
                {idx === Math.floor(post.sections.length / 2) - 1 &&
                  post.pullQuote && (
                    <>
                      <blockquote className="my-10 relative pl-6 border-l-2 border-[#00A3FF]">
                        <Quote className="absolute -left-3 -top-1 w-6 h-6 text-[#00A3FF] bg-[#05070A] p-1 rounded-full" />
                        <p className="text-xl md:text-2xl text-white font-medium leading-snug">
                          {post.pullQuote}
                        </p>
                      </blockquote>

                      <Card className="my-10 p-6 md:p-8 bg-gradient-to-br from-[#071A33] to-[#05070A] border border-[#00A3FF]/30">
                        <h3 className="text-xl md:text-2xl font-bold text-white">
                          See how this could work for your business
                        </h3>
                        <p className="mt-2 text-slate-300/90">
                          Book a free 20-minute review with a Neil AI specialist.
                          We'll walk through your operations and show you where AI
                          and robotics actually fit.
                        </p>
                        <div className="mt-5 flex flex-col sm:flex-row gap-3">
                          <Button
                            asChild
                            className="bg-[#00A3FF] hover:bg-[#0057FF] text-white"
                          >
                            <Link to="/#contact">Book Free Review</Link>
                          </Button>
                          <Button
                            asChild
                            variant="outline"
                            className="border-white/20 text-white hover:bg-white/10 hover:text-white"
                          >
                            <Link to="/robots">View Robot Solutions</Link>
                          </Button>
                        </div>
                      </Card>
                    </>
                  )}
              </section>
            ))}
          </div>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <Badge
                  key={t}
                  className="bg-white/5 text-slate-300 border border-white/10 hover:bg-white/10"
                >
                  {t}
                </Badge>
              ))}
            </div>
          )}

          {/* Related */}
          {related.length > 0 && (
            <section className="mt-16 md:mt-20" aria-labelledby="related-heading">
              <h2
                id="related-heading"
                className="text-2xl md:text-3xl font-bold text-white mb-6"
              >
                Related articles
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((rp) => (
                  <Link
                    key={rp.slug}
                    to={`/blog/${rp.slug}`}
                    className="group block rounded-2xl"
                  >
                    <Card className="h-full overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/10 hover:border-[#00A3FF]/50 transition-all duration-300">
                      <div className="h-40 overflow-hidden">
                        <img
                          src={rp.image}
                          alt={rp.imageAlt}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <Badge className="bg-black/60 text-[#00A3FF] border border-[#00A3FF]/30 mb-2">
                          {rp.category}
                        </Badge>
                        <h3 className="text-base font-bold text-white line-clamp-2">
                          {rp.title}
                        </h3>
                        <p className="text-xs text-slate-400 mt-2">
                          {rp.readTime}
                        </p>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Final CTA */}
          <section className="mt-16">
            <Card className="relative overflow-hidden p-6 md:p-10 bg-gradient-to-br from-[#0057FF]/20 via-[#05070A] to-[#05070A] border border-[#00A3FF]/40">
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Ready to see what Neil AI can do for your team?
              </h2>
              <p className="mt-3 text-slate-300/90 max-w-2xl">
                Book a demo with a specialist. We'll show you what AI
                automation and PUDU robotics look like in a business like yours.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#00A3FF] hover:bg-[#0057FF] text-white"
                >
                  <Link to="/#contact">Book a Demo</Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to="/blog">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to all articles
                  </Link>
                </Button>
              </div>
            </Card>
          </section>

          <div className="h-24 md:h-0" aria-hidden />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPost;
